import { AbstractComponent } from "appworks/components/abstract-component";
import { FlexiText } from "appworks/graphics/elements/flexi-text";
import { Layers } from "appworks/graphics/layers/layers";
import { Services } from "appworks/services/services";
import { Contract } from "appworks/utils/contracts/contract";
import { FreespinWinResult } from "slotworks/model/gameplay/records/results/freespin-win-result";
import { LineResult } from "slotworks/model/gameplay/records/results/line-result";
import { WaysResult } from "slotworks/model/gameplay/records/results/ways-result";

enum FooterMessageKey {
    DEFAULT = "fot.new",
    NO_WIN = "fot.no",
    SPIN_START = "fot.pla",
    FREESPIN_START = "fot.fpl",
    GAME_WIN = "fot.gam",
    LINE_WIN = "fot.lin",
    WAY_WIN = "fot.way",
    WAYS_WIN = "fot.ways",
    WAY_TRIGGER = "fot.tri",
    WAYS_TRIGGER = "fot.trs",
    RETRIGGER = "fot.ret",
    FREESPINS = "fot.fre"
}

export class FooterComponent extends AbstractComponent {
    public hideTotalWin: boolean = false;

    protected layer: Layers;

    protected textValue: string = "";
    protected text: FlexiText;

    constructor(layer?: Layers) {
        super();

        this.layer = layer || Layers.get("Footer");
        this.layer.onSceneEnter.add(() => this.refresh());

        this.layer.defaultScene().execute();

        this.refresh();

        this.showDefaultText();
    }

    public showNoWin() {
        if (Services.translations.exists(FooterMessageKey.NO_WIN)) {
            this.setText(Services.translations.get(FooterMessageKey.NO_WIN));
        } else {
            this.clear();
        }
    }

    public showSpinStart(betValue: number) {
        this.setText(Services.translations.get(FooterMessageKey.SPIN_START, { bet: Services.currency.format(betValue) }));
    }

    public showFreespinStart() {
        const id = FooterMessageKey.FREESPIN_START;
        const message = Services.translations.get(id);

        if (id !== message) {
            this.setText(message);
        } else {
            this.clear();
        }
    }

    public showGameWin(winValue: number) {
        if (!this.hideTotalWin) {
            this.setText(Services.translations.get(FooterMessageKey.GAME_WIN, { win: Services.currency.format(winValue) }));
        }
        return Contract.empty();
    }

    public showLineWin(result: LineResult, symbol?: string) {
        const win = Services.currency.format(result.cashWon);
        this.setText(Services.translations.get(FooterMessageKey.LINE_WIN, { line: result.line, linewin: win, win, matches: result.matches, symbol }));
        return Contract.empty();
    }

    public showWaysWin(result: WaysResult, symbol: string) {
        const winData = {
            payout_per_symbols: Services.currency.format(result.perWayPayout),
            number_of_ways: result.ways,
            total_payout: Services.currency.format(result.cashWon),
            triggered: 0
        };

        if (result.linkedResult instanceof FreespinWinResult) {
            winData.triggered = result.linkedResult.freespinsWon;
        }

        let message = result.ways > 1 ? FooterMessageKey.WAYS_WIN : FooterMessageKey.WAY_WIN;

        if (winData.triggered > 0) {
            message = result.ways > 1 ? FooterMessageKey.WAYS_TRIGGER : FooterMessageKey.WAY_TRIGGER;
        }

        this.setText(Services.translations.get(message, winData));
    }

    public showMiscWin(textId: string) {
        this.setText(Services.translations.get(textId));
    }

    public showFreeSpinsTriggered(freespinsWon?: number) {
        const id = FooterMessageKey.FREESPINS;
        const message = Services.translations.get(id, { freespins: freespinsWon });

        if (id !== message) {
            this.setText(message);
        }
    }

    public showFreeSpinsRetriggered(freespinsWon?: number) {
        const id = FooterMessageKey.RETRIGGER;
        const message = Services.translations.get(id, { freespins: freespinsWon, SPINS: freespinsWon });

        if (id !== message) {
            this.setText(message);
        }
    }

    public showCustomMessage(message: string) {
        this.setText(message);
    }

    public showBigWinMessage(tier: number) {
        // Custom implementation
    }

    public showDefaultText() {
        this.setText(Services.translations.get(FooterMessageKey.DEFAULT));
    }

    public clear() {
        this.setText("");
    }

    protected setText(text: string) {
        this.textValue = text;
        if (this.text) {
            this.text.setText(text);
        }
    }

    protected refresh() {
        this.text = this.layer.getFlexiText("value");
        this.setText(this.textValue);
    }
}
